import { useState } from "react";
import { FaUserPlus, FaEye, FaEyeSlash, FaCheck } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import LoadingOverlay from "../../components/loadingOverlay";
import Select from "react-select";
import countryList from "react-select-country-list";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";

export default function RegisterPage() {
  const navigate = useNavigate();
  const countryOptions = countryList().getData();

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    gender: "",
    dob: "",
    country: "MY", // default Malaysia
    phone: "",
    wsfSpin: "",
    wsfExpiry: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const { showToast, ToastContainer } = useToast();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit =
    form.fullName.trim() &&
    form.email.trim() &&
    form.password.length >= 6 &&
    form.password === form.confirmPassword;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);

    try {
      const payload = {
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        password: form.password,
        gender: form.gender,
        dob: form.dob,
        country: form.country,
        phone: form.phone,
        wsfSpin: form.wsfSpin,
        wsfExpiry: form.wsfExpiry,
      };

      const res = await fetch(`${API_BASE}/api/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) throw new Error(data?.message || "Registration failed.");

      let countdown = 5; // seconds before redirect
      // Initial toast
      showToast(
        `Registration successful!\nPlease check your email for the 6-digit verification code.\nRedirecting to Dashboard in ${countdown}s...`,
        "success"
      );

      // create an interval to update the countdown live
      const interval = setInterval(() => {
        countdown--;
        showToast(
          `Registration successful!\nPlease check your email for the 6-digit verification code.\nRedirecting to Dashboard in ${countdown}s...`,
          "success",
          "top",
          true,
          5500
        );
        if (countdown <= 0) clearInterval(interval);
      }, 1000);

      // Redirect after timer finishes
      setTimeout(() => {
        clearInterval(interval);
        navigate("/verify", { state: { email: form.email } });
      }, 5000);
    } catch (err) {
      showToast(err.message || "Failed to register.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="w-full flex-grow flex flex-col">
      {/* Title */}
      <div className="w-full max-w-3xl px-4 mx-auto text-center pt-10">
        <h1 className="text-3xl sm:text-2xl flex items-center justify-center gap-2 text-gray-800">
          <FaUserPlus className="text-blue-600" />
          Register Account
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow p-5 mt-6 mb-4 space-y-5 max-w-2xl mx-auto w-full"
      >
        {/* Full Name */}
        <div className="text-left">
          <label className="block text-sm font-medium mb-1">
            Full Name <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <input
            type="text"
            name="fullName"
            value={form.fullName}
            onChange={handleChange}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        {/* Email */}
        <div className="text-left">
          <label className="block text-sm font-medium mb-1">
            Email <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        {/* Password */}
        <div className="text-left relative">
          <label className="block text-sm font-medium mb-1">
            Password <span className="text-red-500 italic text-xs">*Required</span>
          </label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              name="password"
              value={form.password}
              onChange={handleChange}
              onBlur={() => setPasswordTouched(true)}
              className={`placeholder:italic placeholder:text-gray-400 w-full border rounded-lg px-3 py-2 pr-10 ${
                passwordTouched && form.password.length > 0 && form.password.length < 6
                  ? "border-red-500"
                  : ""
              }`}
              placeholder="At least 6 characters"
            />
            <button
              type="button"
              onClick={() => setShowPw(!showPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              tabIndex={-1}
            >
              {showPw ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {passwordTouched && form.password.length > 0 && form.password.length < 6 && (
            <p className="text-red-500 text-xs mt-1">
              Password must be at least 6 characters.
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div className="text-left relative">
          <label className="block text-sm font-medium mb-1">
            Confirm Password <span className="text-red-500 italic text-xs">*Required</span>
          </label>

          <div className="relative">
            <input
              type={showConfirmPw ? "text" : "password"}
              name="confirmPassword"
              value={form.confirmPassword}
              onChange={handleChange}
              onBlur={() => setConfirmTouched(true)}
              className={`placeholder:italic placeholder:text-gray-400 w-full border rounded-lg px-3 py-2 pr-14 ${
                confirmTouched &&
                form.confirmPassword &&
                form.password !== form.confirmPassword
                  ? "border-red-500"
                  : ""
              }`}
              placeholder="Confirm Password"
            />

            {/* ✅ Green check icon — left of the eye */}
            {form.password &&
              form.confirmPassword &&
              form.password === form.confirmPassword && (
                <FaCheck className="absolute right-9 top-1/2 -translate-y-1/2 text-green-500" />
              )}

            {/* 👁 Eye toggle */}
            <button
              type="button"
              onClick={() => setShowConfirmPw(!showConfirmPw)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
              tabIndex={-1}
            >
              {showConfirmPw ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {confirmTouched &&
            form.confirmPassword &&
            form.password !== form.confirmPassword && (
              <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
            )}
        </div>

        {/* Gender */}
        <div className="text-left">
          <label className="block text-sm font-medium mb-1">
            Gender <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <Select
            options={[
              { value: "Male", label: "Male" },
              { value: "Female", label: "Female" },
            ]}
            value={
              form.gender
                ? { value: form.gender, label: form.gender }
                : null
            }
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, gender: opt ? opt.value : "" }))
            }
            classNamePrefix="react-select"
            placeholder="Select Gender"
          />
        </div>

        {/* Date of Birth */}
        <div className="text-left">
          <label className="block text-sm font-medium mb-1">
            Date of Birth <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="date"
            name="dob"
            value={form.dob}
            onChange={handleChange}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        {/* Country */}
        <div className="text-left">
          <label className="block text-sm font-medium mb-1">
            Country <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <Select
            options={countryOptions}
            value={countryOptions.find((c) => c.value === form.country) || null}
            onChange={(opt) =>
              setForm((prev) => ({ ...prev, country: opt.value }))
            }
            classNamePrefix="react-select"
            placeholder="Select Country"
          />
        </div>

        {/* Phone */}
        <div className="text-left">
          <label className="block text-sm font-medium mb-1">
            Phone <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                phone: e.target.value.replace(/\D/g, ""),
              }))
            }
            placeholder="0123456789"
            className="w-full border rounded-lg px-3 py-2 placeholder:italic placeholder:text-gray-400"
          />
        </div>

        {/* WSF SPIN (optional) */}
        <div className="text-left">
          <label className="flex text-sm font-medium mb-1 items-center gap-1">
            WSF SPIN

            {/* Tooltip wrapper */}
            <div className="relative group">
              {/* Icon */}
              <div
                className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 text-gray-700 text-xs cursor-pointer"
              >
                ?
              </div>

              {/* Tooltip on hover */}
              <div className="absolute mt-2 w-56 p-2 rounded-md text-xs text-white bg-gray-800 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                Squash Personal Identification Number from the World Squash Federation (WSF)
              </div>
            </div>

            <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="text"
            name="wsfSpin"
            value={form.wsfSpin}
            onChange={handleChange}
            placeholder="e.g. 123456"
            className="w-full border rounded-lg px-3 py-2 placeholder:italic placeholder:text-gray-400"
          />
        </div>

        {/* SPIN Expiry (optional) */}
        <div className="text-left">
          <label className="block text-sm font-medium mb-1">
            SPIN Expiry <span className="italic text-gray-400 text-xs">(Optional)</span>
          </label>
          <input
            type="date"
            name="wsfExpiry"
            value={form.wsfExpiry}
            onChange={handleChange}
            className="w-full border rounded-lg px-3 py-2"
          />
        </div>

        {/* Register Button */}
        <button
          type="submit"
          disabled={!canSubmit || submitting}
          className={`w-full px-5 py-2 rounded-lg text-white ${
            !canSubmit || submitting
              ? "bg-gray-400"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {submitting ? "Registering..." : "Register"}
        </button>

        {/* Small Links */}
        <div className="mt-3 text-xs text-gray-600 text-left flex flex-col gap-1">
          <span>
            Already have an account?{" "}
            <Link to="/login" className="text-blue-600 hover:underline">
              Login here
            </Link>
          </span>
          <span>
            Forgot password?{" "}
            <Link to="/forgot-password" className="text-blue-600 hover:underline">
              Reset it here
            </Link>
          </span>
        </div>
      </form>

      <LoadingOverlay show={submitting} text="Loading..." />
      <ToastContainer />
    </div>
  );
}
